import type { Product } from '@mc/api'
import { convertKeysToSnakeCase } from '@mc/products'
import {
  EventTypes,
  trackGoldStandardEvent,
  getPageSource,
  type PrePaywallPageSource,
} from '@mc/track-event'
import { getMetaAddToCartProps } from '../../lib/helpers'

export interface PurchaseClickProps {
  action: string
  location: string
  /**
   * Where the CTA navigates. Included in the "Purchase Click" payload.
   */
  href?: string
  /**
   * Explicit page source for the "Purchase Click" event. When omitted the
   * inferred `getPageSource()` value is used.
   */
  pageSource?: PrePaywallPageSource | (string & {})
  /**
   * Plan class for the "Purchase Click" payload. Omitted from the payload when
   * not provided; pass 'annual-pass' for all-access CTAs or a class slug for
   * class-marketing pages.
   */
  class?: string | null
  /**
   * Overrides the default `content_type: 'product'`.
   */
  contentType?: string
  /**
   * Any additional "Purchase Click" properties (e.g. camp `class_type`,
   * `page_type`, nav `level`/`product`). Keys are snake-cased before firing.
   * On /promotion they ride on "Promo Page Click" instead.
   */
  purchaseClickProps?: Record<string, unknown>
  /**
   * The annual-pass (default tier) product, read from static data at the call
   * site. Used to derive `product_id`/`contents` when the Meta catalog cookie
   * is absent. Not sent to the payload directly.
   */
  annualPass?: Product | null
  /**
   * Whether this CTA starts a gift purchase. Selects the gift vs. regular
   * pre-purchase LTV for `contents[].price`. Defaults to false (regular).
   */
  isGift?: boolean
}

/**
 * Fires "Purchase Click" as a gold-standard event — ad partners enabled, with
 * the active-annual-member downgrade.
 *
 * Internal to this package: callers use `trackGoldStandardPurchaseClick`, which
 * also routes /promotion CTAs to "Promo Page Click".
 */
export const trackPurchaseClick = ({
  action,
  location,
  href,
  pageSource,
  class: planClass,
  contentType,
  purchaseClickProps,
  annualPass,
  isGift = false,
}: PurchaseClickProps) =>
  trackGoldStandardEvent(EventTypes.PURCHASE_CLICK, {
    action,
    href,
    location,
    page_source: pageSource ?? getPageSource(),
    page_path:
      typeof window !== 'undefined' ? window.location.pathname : undefined,
    ...getMetaAddToCartProps(annualPass, isGift, contentType),
    ...(planClass && { class: planClass }),
    ...(purchaseClickProps && convertKeysToSnakeCase(purchaseClickProps)),
  })
